/* Playing a span of the archive, which arrives as a row of separate objects.
 *
 * `ClipPlayer` fetches one object whole and decodes it whole, and is right to:
 * a clip is seconds long. A brushed selection is not. One 60 s segment is
 * ~10 MB of FLAC and about **23 MB decoded** to Float32 stereo, so ten minutes
 * is ~100 MB on the wire and ~230 MB in memory. Decoding that up front is a
 * tab crash on a phone, which is what this module exists to avoid.
 *
 * Three properties shape it.
 *
 * **A bounded window.** At most a few segments are decoded at once and each is
 * released as the playhead passes it, so peak memory is flat regardless of how
 * long the selection is.
 *
 * **Placed by recorded time, never concatenated.** Each segment is scheduled at
 * the instant it was recorded. A missing minute inside a selection therefore
 * plays as a minute of silence rather than being spliced out. Rule 4 says a gap
 * is a fact and is never hidden; splicing would hide it in the one medium where
 * the listener cannot see the join.
 *
 * **One gain for the whole span.** Normalisation is measured once, on the first
 * segment that decodes, and then held. Re-solving per segment would make a
 * quiet minute and a loud minute sound the same, which destroys exactly the
 * information someone is listening for -- the same argument live.ts makes about
 * fixing capture gain before recording starts.
 *
 * That last paragraph described an intention for some time and not the code.
 * Nothing measured anything, `setGainDb` was exported and never called, and the
 * archive therefore played at unity -- which for content sitting at -56 to -80
 * dBFS is silence. A person scrubbing back through yesterday heard nothing and
 * had every reason to conclude the sea had been quiet. It is implemented below,
 * with the same targets ClipPlayer uses, so the same minute sounds the same
 * whichever of the two opens it.
 */
import { objectUrls, type SignedObject } from './api'
import { listeningGainDb, measureLevels } from './dsp'

export interface Segment {
  key: string
  /** Epoch seconds when this segment's audio starts. */
  startUtc: number
  durationS: number
  size: number | null
  kind: string
}

export interface SegmentPlayerOpts {
  missionId: string
  /** Sorted by startUtc. */
  segments: Segment[]
  /** Decoded segments held ahead of the playhead. */
  lookahead?: number
  onTime?: (epoch: number) => void
  onState?: (s: PlayerState) => void
  /** Called once per segment that will not decode, with the key. */
  onUndecodable?: (key: string) => void
  /** The measured lift, in dB, once the first segment has decoded. Surfaced so
   *  a page can say what it did rather than quietly making the sea louder. */
  onGain?: (db: number) => void
  /** Start filtered. Changed later with `setBand`. */
  band?: Band | null
  /* An AudioContext to borrow instead of making one.
   *
   * This is not an optimisation. A browser only lets an AudioContext start
   * from a user gesture, and the path into this module is: press the strip ->
   * fetch the day's objects -> build a player. The fetch ends the gesture, so
   * a context created here is born suspended and resume() is refused --
   * silence, on exactly the platform (WebKit) where it is hardest to notice,
   * because the clock still runs. live.ts learned this the same way and its
   * comment is worth reading.
   *
   * So the page makes one in the click handler and lends it. Also the reason
   * `destroy` does not close what it did not open: browsers cap concurrent
   * contexts at around six, and a page that opened one per span would run out
   * after a few minutes of scrubbing. */
  context?: AudioContext
  /** Called once with the analyser, so a page can draw what is playing. */
  onAnalyser?: (a: AnalyserNode) => void  /** Signs keys instead of `objectUrls`. A shared link has no session and no
   *  mission to sign against; its token is the authority, so it brings its
   *  own signer. Same contract: what it cannot sign comes back rejected. */
  sign?: (keys: string[]) => Promise<{
    urls: { key: string; url: string }[]
    rejected: { key: string }[]
  }>
}

export type PlayerState = 'idle' | 'buffering' | 'playing' | 'paused' | 'ended'

/** A band-pass, in Hz, or null for everything. Same shape as live.ts, and
 *  deliberately the same behaviour: the archive and the live stream must not
 *  answer "what does the fish hear" differently. */
export interface Band { lo: number; hi: number }

const BAND_OPEN: Band = { lo: 1, hi: 24000 }
const BAND_GLIDE_S = 0.08

/** URLs are re-signed when fewer than this many seconds of validity remain. */
const RESIGN_MARGIN_S = 60
const SIGN_BATCH = 64

/** A safety limiter for the way to the speaker. Shared with `ClipPlayer`.
 *
 *  Same node and same settings as the live graph, and it belongs on every path
 *  that boosts: the gain exists to make a signal 60 dB down audible, and the
 *  events worth hearing are transients -- the node once measured one 78 dB
 *  above its own noise floor. Boosted enough to hear the quiet parts, that
 *  arrives in someone's headphones on a boat.
 *
 *  The archive paths had no limiter at all while live did, which mattered more
 *  here than there: live is where you happen to be listening, and the archive
 *  is where you go looking for exactly these transients on purpose.
 *
 *  Placed after the analyser, so it shapes what is heard and never what is
 *  measured, drawn or saved.
 */
export function createListeningLimiter(ctx: BaseAudioContext): DynamicsCompressorNode {
  const limiter = ctx.createDynamicsCompressor()
  limiter.threshold.value = -6
  limiter.knee.value = 0
  limiter.ratio.value = 20
  limiter.attack.value = 0.002
  limiter.release.value = 0.15
  return limiter
}

export interface SegmentPlayer {
  play: () => Promise<void>
  pause: () => void
  stop: () => void
  seek: (epoch: number) => void
  /** Epoch seconds, or null before playback has started. */
  now: () => number | null
  /** On top of the measured normalisation, not instead of it. */
  setGainDb: (db: number) => void
  setBand: (b: Band | null) => void
  destroy: () => void
}

/** Which captured channel carries the calibrated signal.
 *
 *  `input.primary_channel` on the node, which defaults to 0 and is 0 across
 *  the fleet. It is not in the object index, so it is assumed here — if a unit
 *  is ever wired the other way this has to start travelling with the audio.
 */
const PRIMARY_CHANNEL = 0

/** Route one channel to both ears, rather than playing the file as stereo.
 *
 *  These recordings are not stereo. The Codec Zero is stereo-only so capture
 *  writes two channels, but there is one hydrophone: `primary_channel` carries
 *  it, and the other is a deliberate low-gain copy for recovering clipped
 *  peaks. Played as-is that is a loud left and a quiet right, which is what an
 *  operator hears as sound in one side of the headset.
 *
 *  A splitter rather than a downmix, and the difference is not stylistic.
 *  Measured on a real segment from this fleet the two channels correlate at
 *  **-0.96** -- the second is 12 dB down *and inverted*, which is what a
 *  balanced input does across tip and ring. Averaging them, which is what
 *  `channelCount = 1` would do, subtracts almost the whole signal. The node
 *  makes the same choice in `reduce.py` and `live.py` for the same reason, and
 *  the three must not diverge.
 *
 *  Mono into a stereo destination is up-mixed to both outputs by Web Audio, so
 *  taking one channel is what actually fills both ears.
 */
export function connectPrimary(ctx: BaseAudioContext, src: AudioNode, dest: AudioNode) {
  const channels = (src as AudioBufferSourceNode).buffer?.numberOfChannels ?? 1
  if (channels <= 1) { src.connect(dest); return }
  const split = ctx.createChannelSplitter(channels)
  src.connect(split)
  split.connect(dest, Math.min(PRIMARY_CHANNEL, channels - 1))
}

export function createSegmentPlayer(o: SegmentPlayerOpts): SegmentPlayer {
  const lookahead = o.lookahead ?? 2
  const segs = [...o.segments].sort((a, b) => a.startUtc - b.startUtc)
  const t0 = segs[0]?.startUtc ?? 0
  const tEnd = segs.length
    ? segs[segs.length - 1]!.startUtc + segs[segs.length - 1]!.durationS
    : 0

  let ctx: AudioContext | null = null
  let gain: GainNode | null = null
  let hp: BiquadFilterNode | null = null
  let lp: BiquadFilterNode | null = null
  let limiter: DynamicsCompressorNode | null = null
  let analyser: AnalyserNode | null = null
  let band: Band | null = o.band ?? null
  let state: PlayerState = 'idle'
  let raf = 0

  /* The measured lift for this span, and the listener's own offset on top.
   *
   * Two numbers rather than one because they answer different questions.
   * `normDb` is "how far below full scale is this recording", solved once from
   * real samples and then frozen for the whole span -- so a quiet minute and a
   * loud minute keep their difference. `userDb` is "and how loud do I want it",
   * which a person may change at any time without disturbing the first. */
  let normDb = 0
  let userDb = 0
  let measured = false

  /** ctx.currentTime that corresponds to epoch `t0`. Re-based on every seek. */
  let anchorCtx = 0
  let anchorEpoch = t0

  /** A seek asked for before there was a clock to hang it on.
   *
   * `seek` can only re-base the anchor once an AudioContext exists, and the
   * context cannot exist until the first `play` -- a browser will not give one
   * out except on a user gesture. So a player that is seeked and then played,
   * which is what opening a span at a moment looks like, used to lose the
   * seek: `play` set the anchor to `t0` and the span began at its start.
   *
   * That was invisible for as long as every route into the archive went
   * through the day strip, because pressing the strip seeks a player that is
   * already running. It stopped being invisible when a link could open the
   * page standing at a moment.
   *
   * Kept as an epoch rather than applied to `anchorEpoch` directly, so that
   * "where the listener asked to be" and "where the clock says we are" stay
   * two different facts right up until there is a clock. */
  let pendingEpoch: number | null = null

  const signed = new Map<string, { url: string; at: number }>()
  const decoded = new Map<string, AudioBuffer>()
  const scheduled = new Map<string, AudioBufferSourceNode>()
  const undecodable = new Set<string>()
  let inflight: Promise<void> | null = null
  let dead = false

  const setState = (s: PlayerState) => { state = s; o.onState?.(s) }
  const epochNow = () =>
    ctx ? anchorEpoch + (ctx.currentTime - anchorCtx) : null

  /** Sign a run of keys, reusing anything still comfortably valid.
   *
   *  Batched because a ten-minute selection is ten objects and an hour is
   *  sixty; one round trip per segment would spend more time signing than
   *  playing. */
  async function ensureSigned(keys: string[]) {
    const stale = keys.filter((k) => {
      const s = signed.get(k)
      return !s || (Date.now() - s.at) / 1000 > 300 - RESIGN_MARGIN_S
    })
    if (!stale.length) return
    for (let i = 0; i < stale.length; i += SIGN_BATCH) {
      const batch = stale.slice(i, i + SIGN_BATCH)
      const r = o.sign ? await o.sign(batch) : await objectUrls(o.missionId, batch)
      const at = Date.now()
      r.urls.forEach((u: Pick<SignedObject, 'key' | 'url'>) => signed.set(u.key, { url: u.url, at }))
      // A rejected key is not fatal to the span. It is one segment that cannot
      // play, and the silence where it would have been is the honest result.
      r.rejected.forEach((x) => undecodable.add(x.key))
    }
  }

  async function decode(seg: Segment): Promise<AudioBuffer | null> {
    if (decoded.has(seg.key)) return decoded.get(seg.key)!
    if (undecodable.has(seg.key)) return null
    const url = signed.get(seg.key)?.url
    if (!url || !ctx) return null
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(`storage returned ${res.status}`)
      const buf = await res.arrayBuffer()
      const audio = await ctx.decodeAudioData(buf)
      decoded.set(seg.key, audio)
      measureOnce(audio)
      return audio
    } catch {
      // Most likely a codec this browser will not decode -- 24-bit FLAC is the
      // candidate. Recorded per key rather than thrown, so one unplayable
      // format degrades the span to silence-with-a-reason instead of killing
      // playback outright, and the caller can offer the proxy or a download.
      undecodable.add(seg.key)
      o.onUndecodable?.(seg.key)
      return null
    }
  }

  /** Apply `normDb + userDb`, gliding rather than stepping. */
  function applyGain() {
    if (!gain || !ctx) return
    const t = ctx.currentTime
    gain.gain.cancelScheduledValues(t)
    gain.gain.setTargetAtTime(Math.pow(10, (normDb + userDb) / 20), t, 0.03)
  }

  function applyBand() {
    if (!hp || !lp || !ctx) return
    const nyquist = ctx.sampleRate / 2 - 1
    const lo = band ? Math.max(1, band.lo) : BAND_OPEN.lo
    const hi = Math.min(band ? band.hi : BAND_OPEN.hi, nyquist)
    hp.frequency.setTargetAtTime(lo, ctx.currentTime, BAND_GLIDE_S)
    lp.frequency.setTargetAtTime(Math.max(hi, lo + 1), ctx.currentTime, BAND_GLIDE_S)
  }

  /* Solve the span's level from the first segment that decodes, then never
   * again.
   *
   * The first segment rather than a survey of all of them, because the survey
   * cannot be done without decoding the whole span -- which is the thing this
   * module exists not to do. One minute of the sea is a good enough estimate of
   * the next hour of it, and being wrong costs a span that is a few dB quiet
   * rather than one that is inaudible.
   *
   * Measured on the primary channel alone. The second channel is a deliberate
   * 12 dB-down inverted copy, so including it would drag the RMS down by about
   * 6 dB and lift every span by that much too much. */
  function measureOnce(buf: AudioBuffer) {
    if (measured) return
    measured = true
    const ch = Math.min(PRIMARY_CHANNEL, buf.numberOfChannels - 1)
    const l = measureLevels(buf.getChannelData(ch))
    // The same rule the live monitor uses, so a span of the archive and the
    // stream it came from arrive at the ear at the same level. Silence gets no
    // boost, which `listeningGainDb` handles: there is no amount of gain that
    // makes a digitally silent minute audible.
    normDb = listeningGainDb(l)
    applyGain()
    o.onGain?.(normDb)
  }

  /** Schedule everything inside the lookahead, and release what is behind. */
  async function pump() {
    if (!ctx || !gain || dead || state !== 'playing') return
    const nowEpoch = epochNow()
    if (nowEpoch === null) return

    // Bounded by segment count rather than by a span in seconds. A window of
    // "the next N minutes" assumes a segment length; this file is handed
    // whatever the node was configured to write, and a proxy of the same span
    // is a different object again.
    const ahead = segs
      .filter((s) => s.startUtc + s.durationS > nowEpoch - 1)
      .slice(0, lookahead + 1)

    await ensureSigned(ahead.map((s) => s.key))

    for (const seg of ahead) {
      if (scheduled.has(seg.key)) continue
      const buf = await decode(seg)
      if (!buf || !ctx || !analyser || state !== 'playing') continue
      const when = anchorCtx + (seg.startUtc - anchorEpoch)
      // Already past: skip rather than start it late, which would play the
      // segment at the wrong instant and drift the whole span.
      if (when + buf.duration <= ctx.currentTime) continue
      const src = ctx.createBufferSource()
      src.buffer = buf
      connectPrimary(ctx, src, analyser)
      const offset = Math.max(0, ctx.currentTime - when)
      src.start(Math.max(ctx.currentTime, when), offset)
      src.onended = () => {
        scheduled.delete(seg.key)
        // Released as the playhead passes, which is what keeps peak memory
        // flat: at most lookahead+1 decoded buffers exist at any moment,
        // however long the selection is.
        decoded.delete(seg.key)
      }
      scheduled.set(seg.key, src)
    }
  }

  function tick() {
    if (dead) return
    const t = epochNow()
    if (t !== null) {
      o.onTime?.(Math.min(t, tEnd))
      if (t >= tEnd) { stop(); setState('ended'); return }
    }
    if (!inflight) {
      inflight = pump().catch(() => {}).finally(() => { inflight = null })
    }
    raf = requestAnimationFrame(tick)
  }

  function clearScheduled() {
    scheduled.forEach((s) => { try { s.stop() } catch { /* already ended */ } })
    scheduled.clear()
    decoded.clear()
  }

  async function play() {
    if (dead) return
    if (!ctx) {
      ctx = o.context ?? new AudioContext()
      gain = ctx.createGain()
      /* The analyser goes first, before the filters, for the same reason it
       * does on the live graph: the picture shows the whole spectrum and the
       * band is drawn on top of it, so a listener can see what they filtered
       * out. Same fftSize too -- 32768, ~1.5 Hz bins at 48 kHz. live.ts has the
       * table; the short version is that resolving a 5-10 Hz band is a fact
       * about window length, and the two halves of the player must not disagree
       * about whether that band is visible. */
      analyser = ctx.createAnalyser()
      analyser.fftSize = 32768
      analyser.smoothingTimeConstant = 0.3
      analyser.minDecibels = -110
      analyser.maxDecibels = 0
      /* The band-pass sits between the sources and the gain, so the level a
       * span is normalised to is the level of the whole recording rather than
       * of whatever slice of it is currently being listened to. Switching from
       * "everything" to a 100-580 Hz band must change what you hear and not how
       * loud the rest of the session is. */
      hp = ctx.createBiquadFilter()
      hp.type = 'highpass'
      hp.Q.value = Math.SQRT1_2
      lp = ctx.createBiquadFilter()
      lp.type = 'lowpass'
      lp.Q.value = Math.SQRT1_2
      limiter = createListeningLimiter(ctx)
      analyser.connect(hp)
      hp.connect(lp)
      lp.connect(gain)
      gain.connect(limiter)
      limiter.connect(ctx.destination)
      applyBand()
      applyGain()
      o.onAnalyser?.(analyser)
      anchorEpoch = pendingEpoch ?? t0
      anchorCtx = ctx.currentTime
      pendingEpoch = null
    }
    // Deliberately no re-basing here. A suspended AudioContext's currentTime
    // does not advance, so the anchor recorded at pause is still the right one
    // when it resumes -- and recomputing it would jump the playhead by however
    // long the pause lasted.
    await ctx.resume()
    setState('playing')
    cancelAnimationFrame(raf)
    raf = requestAnimationFrame(tick)
  }

  function pause() {
    if (!ctx) return
    const t = epochNow()
    clearScheduled()
    void ctx.suspend()
    if (t !== null) { anchorEpoch = t; anchorCtx = ctx.currentTime }
    cancelAnimationFrame(raf)
    setState('paused')
  }

  function stop() {
    clearScheduled()
    cancelAnimationFrame(raf)
    pendingEpoch = null
    if (ctx) { anchorEpoch = t0; anchorCtx = ctx.currentTime }
    setState('idle')
  }

  function seek(epoch: number) {
    const target = Math.max(t0, Math.min(epoch, tEnd))
    clearScheduled()
    if (ctx) {
      anchorEpoch = target
      anchorCtx = ctx.currentTime
      pendingEpoch = null
    } else {
      pendingEpoch = target
    }
    o.onTime?.(target)
  }

  return {
    play, pause, stop, seek,
    now: () => epochNow(),
    // On top of the measured lift, never replacing it -- so a listener turning
    // the volume down does not also undo the normalisation and land back at
    // inaudible. Ramped rather than stepped: an instant gain change on a live
    // graph is an audible click, and 30 ms is the figure ClipPlayer settled on.
    setGainDb: (db) => { userDb = db; applyGain() },
    setBand: (b) => { band = b; applyBand() },
    destroy: () => {
      dead = true
      clearScheduled()
      cancelAnimationFrame(raf)
      // Disconnected, never closed when it was borrowed: a closed context is
      // dead for good, and the page's one context has to outlive every span
      // played through it.
      for (const n of [analyser, hp, lp, gain, limiter]) {
        try { n?.disconnect() } catch { /* already detached */ }
      }
      if (ctx && !o.context) void ctx.close()
      ctx = null
      gain = null
      hp = null
      lp = null
      limiter = null
      analyser = null
    },
  }
}
